<template>
  <v-container>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <Editor
      v-if="!isEditMode || (isEditMode && initialData)"
      ref="editor"
      :title="title"
      :initial-data="initialData"
      :loading="loading"
      type="post-op"
      :is-edit-mode="isEditMode"
      @save="confirmSave"
      @cancel="confirmCancel"
      @delete="confirmDelete"
      @show-error="showError"
    />

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog
      ref="deleteDialog"
      v-model="dialogs.delete.show"
      :title="dialogs.delete.title"
      :content="dialogs.delete.content"
      titleClass="justify-center font-weight-bold text-h5 red-6--text"
      confirm-btn-text="確認刪除"
      confirm-btn-color="red-6"
      maxWidth="380"
      @confirm="deleteItem"
    />

    <!-- Unsaved Changes Dialog -->
    <ConfirmDialog
      v-model="dialogs.cancel.show"
      title="您所做的變更尚未儲存，確定要離開嗎？"
      content=""
      titleClass="justify-center font-weight-bold text-h5 tiffany-9--text"
      contentClass="mt-2 justify-center d-flex text-center"
      confirm-btn-text="繼續編輯"
      confirm-btn-color="tiffany-9"
      cancelBtnText="不儲存並離開"
      maxWidth="380"
      persistent
      @confirm="dialogs.cancel.show = false"
      @cancel="goBack(false)"
    />

    <!-- Name Change Warning Dialog -->
    <ConfirmDialog
      v-model="dialogs.nameChange.show"
      title="確定要變更名稱嗎？"
      content="變更訊息名稱後，請同步更正健保系統約診備註中的 #名稱#，後續同步才會依新名稱辨識。已建立的排程不會因改名而重建。"
      titleClass="justify-center font-weight-bold text-h5 tiffany-9--text"
      contentClass="mt-2 justify-center d-flex text-center"
      confirm-btn-text="確定變更"
      confirm-btn-color="tiffany-9"
      cancelBtnText="取消"
      maxWidth="380"
      persistent
      @confirm="proceedSaveAfterNameChange"
      @cancel="cancelNameChange"
    />

    <!-- Success Dialogs (Create/Update) -->
    <ConfirmDialog
      v-model="dialogs.success.show"
      :title="dialogs.success.title"
      :content="dialogs.success.content"
      titleClass="justify-center font-weight-bold text-h5 tiffany-9--text"
      contentClass="mt-2 justify-center d-flex text-center"
      :confirm-btn-text="canAccess('home') ? '點我前往' : ''"
      confirm-btn-color="tiffany-9"
      cancelBtnText="返回列表"
      maxWidth="380"
      persistent
      @confirm="goToAppointmentPage"
      @cancel="goBack(false)"
    >
      <template v-slot:content>
        <v-card-text class="mt-2 text-center">
          <div class="text-pre-line">{{ dialogs.success.content }}</div>
          <div v-if="dialogs.success.tagName" class="care-tag-notice mt-3">
            智能個管師標籤「{{ dialogs.success.tagName }}」已同步建立，<br>
            可在約診小卡直接勾選
          </div>
        </v-card-text>
      </template>
    </ConfirmDialog>

    <v-snackbar v-model="snackbar.show" centered timeout="2000" color="red-6" dark>
      {{ snackbar.text }}
    </v-snackbar>

  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import Editor from '@/components/HealthEducation/Editor.vue';
import ConfirmDialog from '@/components/utils/ConfirmDialog.vue';
import { isHourMode } from '@/utils/careSendTiming';
import {
  getSingleCustomizeCareTagAPI,
  createCustomizeCareTagAPI,
  updateCustomizeCareTagAPI,
  deleteCustomizeCareTagAPI
} from '@/api';

export default {
  name: 'PostOperativeCareTemplateForm',
  components: { Editor, ConfirmDialog },
  data() {
    return {
      loading: false,
      initialData: null,
      initialDataString: '',
      initialTitle: '',
      pendingSaveData: null,
      snackbar: {
        show: false,
        text: ''
      },
      dialogs: {
        delete: {
          show: false,
          title: '您確定要刪除嗎？',
          content: ''
        },
        cancel: {
          show: false,
        },
        nameChange: {
          show: false,
        },
        success: {
          show: false,
          title: '',
          tagName: '',
          content: '歡迎前往約診頁面，\n開始設定智能個管師發送排程。'
        }
      }
    };
  },
  computed: {
    ...mapGetters(['axiosParamData', 'canAccess']),
    isEditMode() {
      return !!this.$route.params.id;
    },
    itemId() {
      return this.$route.params.id;
    },
    title() {
      return this.isEditMode ? '編輯智能個案管理師訊息' : '新增智能個案管理師訊息';
    },
    isDirty() {
      if (this.loading || !this.$refs.editor) return false;
      return JSON.stringify(this.$refs.editor.healthEducation) !== this.initialDataString;
    }
  },
  methods: {
    goBack(checkDirty = true) {
      if (checkDirty && this.isDirty) {
        this.dialogs.cancel.show = true;
      } else {
        this.$router.push({ name: 'CareTemplateList' });
      }
    },
    goToAppointmentPage() {
      this.$router.push({ name: 'home' });
    },
    confirmCancel() {
      this.goBack();
    },
    showError(message) {
      this.snackbar.text = message;
      this.snackbar.show = true;
    },
    setInitialData(data) {
      this.initialData = data;
      this.initialDataString = JSON.stringify(data);
      this.initialTitle = data?.title || '';
    },
    async getItem() {
      this.loading = true;
      try {
        const res = await getSingleCustomizeCareTagAPI(this.itemId, this.axiosParamData);
        const item = res.data.data;
        if (item) {
          const formattedData = {
            id: item.id,
            title: item.tag_name,
            messages: item.message_templates.map(t => ({
              id: t.id || `tmp_${Date.now()}`,
              type: t.message_type,
              content: {
                text: t.message_content,
                json: t.message_type === 'line_flex' ? t.message_content : null,
                images: t.file || [],
                start_offset_days: t.start_offset_days,
                start_offset_hours: isHourMode(t) ? t.start_offset_hours : null,
                send_at_time: t.send_at_time,
                title: t.title || '',
                offset_direction: t.offset_direction || 'AFTER',
                offset_unit: isHourMode(t) ? 'HOUR' : 'DAY',
              }
            }))
          };
          this.setInitialData(JSON.parse(JSON.stringify(formattedData)));
        }
      } catch (error) {
        console.error('Failed to fetch item:', error);
        if (error.response && error.response.status === 404) {
          this.showError('項目不存在或已被刪除');
          setTimeout(() => this.goBack(false), 2000);
        } else {
          this.showError('讀取資料時發生錯誤');
        }
      } finally {
        this.loading = false;
      }
    },
    cleanData(data) {
      const formData = new FormData();
      formData.append('tag_name', data.title);
      for (const [key, value] of Object.entries(this.axiosParamData)) {
        formData.append(key, value);
      }

      data.messages.forEach((message, index) => {
        if (
          (typeof message.id === "number") ||
          (typeof message.id === "string" && !message.id.startsWith("tmp"))
        ) {
          formData.append(`message_templates[${index}][id]`, message.id);
        }

        // offset_unit 只存在畫面：小時模式送小時數與 0 天；天模式送空字串（後端轉 null）
        const isHour = message.content.offset_unit === 'HOUR';
        formData.append(`message_templates[${index}][title]`, message.content.title || '');
        formData.append(`message_templates[${index}][start_offset_days]`, isHour ? 0 : (message.content.start_offset_days || 1));
        formData.append(`message_templates[${index}][start_offset_hours]`, isHour ? Number(message.content.start_offset_hours) : '');
        formData.append(`message_templates[${index}][send_at_time]`, message.content.send_at_time || '09:00');
        formData.append(`message_templates[${index}][offset_direction]`, message.content.offset_direction || 'AFTER');
        formData.append(`message_templates[${index}][message_type]`, message.type);
        formData.append(`message_templates[${index}][message_content]`, message.type === 'line_flex' ? message.content.json : message.content.text);

        if (message.content.images && message.content.images.length > 0) {
          message.content.images.forEach((file, fileIndex) => {
            formData.append(`message_templates[${index}][file][${fileIndex}]`, file);
          });
        }
      });

      return formData;
    },
    confirmSave(data) {
      if (this.isEditMode && data.title !== this.initialTitle) {
        this.pendingSaveData = data;
        this.dialogs.nameChange.show = true;
        return;
      }
      this.save(data);
    },
    proceedSaveAfterNameChange() {
      this.dialogs.nameChange.show = false;
      const data = this.pendingSaveData;
      this.pendingSaveData = null;
      if (data) {
        this.save(data);
      }
    },
    cancelNameChange() {
      this.dialogs.nameChange.show = false;
      this.pendingSaveData = null;
    },
    async save(data, leaveAfterSave = false) {
      this.loading = true;
      const payload = this.cleanData(data);

      try {
        if (this.isEditMode) {
          payload.append('_method', 'put');
          await updateCustomizeCareTagAPI(this.itemId, payload);
          this.setInitialData(JSON.parse(JSON.stringify(data)));
          this.dialogs.success.title = '更新成功';
          this.dialogs.success.tagName = '';
          this.dialogs.success.show = true;
        } else {
          await createCustomizeCareTagAPI(payload);
          this.dialogs.success.title = '建立成功';
          this.dialogs.success.tagName = data.title;
          this.dialogs.success.show = true;
        }
      } catch (error) {
        console.error('Failed to save:', error);
        this.showError(error?.response?.data?.message || '操作失敗');
      } finally {
        this.loading = false;
      }
    },
    async saveAndLeave() {
      this.dialogs.cancel.show = false;
      await this.save(this.$refs.editor.healthEducation, true);
    },
    confirmDelete() {
      this.dialogs.delete.show = true;
    },
    async deleteItem() {
      this.loading = true;
      try {
        await deleteCustomizeCareTagAPI(this.itemId, this.axiosParamData);
        this.showError('刪除成功');
        this.goBack(false);
      } catch (error) {
        console.error('Failed to delete:', error);
        this.showError(error?.response?.data?.message || '刪除失敗');
      }
      finally {
        this.loading = false;
        this.dialogs.delete.show = false;
      }
    }
  },
  created() {
    if (this.isEditMode) {
      this.getItem();
    }
  },
   mounted() {
    if (!this.isEditMode) {
       // Ensure editor is mounted and get initial state
       this.$nextTick(() => {
         this.setInitialData(JSON.parse(JSON.stringify(this.$refs.editor.healthEducation)));
       });
    }
  }
};
</script>

<style scoped>
.care-tag-notice {
  padding: 8px 12px;
  border-radius: 4px;
  background: #eef8f6;
  color: #333;
  font-size: 14px;
  line-height: 1.7;
  overflow-wrap: anywhere;
}
</style>
